import React from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import EyeIcon from 'assets/svg/eye.svg';
import EditIcon from 'assets/svg/edit-pencil.svg';
import { useLocale } from 'locale';
import { Route } from 'config/routes';
import { BreakpointValue, useScreenBreakpoint } from 'consts/breakpoints';
import { BulletinStatusType, IBulletin, ICommercialBulletin } from 'utils/entities';
import { RootAction, State } from 'store/state';
import { PromotePrivateBulletinViewSource } from 'store/state/app/private-user-monetization/actions';
import { editListingStatusAction } from 'store/state/uploadBulletin/actions';
import { Button } from 'ds/components/button';
import { Link } from 'components/link';
import ManualPromoteBulletinButton from 'components/private-bulletin-promotion/manual-promote-button';
import UpgradePlanButton from './UpgradePlanButton';
import { currentPromotionPlanIsAboutToExpire, isBasicPromotionSelector } from 'store/state/app/private-user-monetization/selectors';
import { PrivateUserMonetizationSource } from 'store/state/app';
import { UpdateListingStatusModalSource } from 'components/update-listing-status-popup/types';

const Root = styled.div(({ theme }) => ({
  background: theme.colors.brand.lightBlue,
  boxShadow: '-1px 0px 0px 0px rgba(0, 0, 0, 0.12), 0px -1px 0px 0px rgba(0, 0, 0, 0.12)',
}));

const Wrapper = styled.div<{ screenBreakpoint: BreakpointValue }>(({ theme, screenBreakpoint }) => ({
  maxWidth: 1440,
  margin: '0 auto',
  flexGrow: 1,
  padding: screenBreakpoint <= 2 ? theme.spacing(2.5) : `${theme.spacing(2.5)} 60px`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
}));

const Group = styled.div<{ screenBreakpoint: BreakpointValue; shouldGrow: boolean }>(({ theme, screenBreakpoint, shouldGrow }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: screenBreakpoint <= 2 ? theme.spacing(1) : theme.spacing(4),
  flex: shouldGrow ? 1 : 'initial',
}));

const GroupItem = styled.div<{ sizeVariant: 'grow' | 'fixed' | null }>(({ sizeVariant }) => ({
  flex: sizeVariant === 'grow' ? 1 : 'initial',
  '& button, .span-button': {
    ...sizeVariant === 'grow' ? { width: '100%' } : undefined,
    ...sizeVariant === 'fixed' ? { width: '230px' } : undefined,
  },
}));


interface PrivateBulletinBarProps {
  bulletin: IBulletin | ICommercialBulletin;
  isFreePromotionPlan: boolean;
  isAboutToExpire: boolean;
  onUnfreezeClick: (bulletinId: string, bulletinType: 'bulletin' | 'commercialBulletin') => void;
}

// Sticky bar for private bulletin owner
const PrivateBulletinBar: React.FC<PrivateBulletinBarProps> = props => {
  const { bulletin, isFreePromotionPlan, onUnfreezeClick, isAboutToExpire } = props;
  const { t } = useLocale();
  const screenBreakpoint = useScreenBreakpoint();

  const isMobile = screenBreakpoint === 1;
  const isFrozenBulletin = bulletin.status.status === 'frozen';
  const editBulletinPageRoute = bulletin.type === 'commercialBulletin' ? Route.EditCommercialBulletin : Route.EditBulletin;

  const isManualPromoteButtonVisible = !isFrozenBulletin;
  const isUpgradePromotionPlanButtonVisible = !isFrozenBulletin && (isFreePromotionPlan || isAboutToExpire);
  const isUnfreezeBulletinButtonVisible = isFrozenBulletin;
  const isEditButtonWithText = !isMobile || (isMobile && !isFreePromotionPlan && !isUpgradePromotionPlanButtonVisible);

  const rightGroupShouldGrow = !isFreePromotionPlan && !isUpgradePromotionPlanButtonVisible;
  const getEditButtonSizeVariant = () => {
    if (isEditButtonWithText && !isMobile) return 'fixed';
    if (rightGroupShouldGrow) return 'grow';
    return null;
  };

  return (
    <Root
      data-auto="private-bulletin-bar"
    >
      <Wrapper screenBreakpoint={screenBreakpoint}>
        <Group
          screenBreakpoint={screenBreakpoint}
          shouldGrow={rightGroupShouldGrow}
        >
          <GroupItem sizeVariant={getEditButtonSizeVariant()}>
            <Link routeName={editBulletinPageRoute} routeParams={{ id: bulletin.id, source: 'property_page' }}>
              <Button
                aria-label={t('privateBulletinStickyBar.editBulletin')}
                mode="ghost"
                size="extraLarge"
                onlyIcon={!isEditButtonWithText && isUpgradePromotionPlanButtonVisible}
                {...{ as: 'span', className: 'span-button' }}
              >
                <EditIcon />
                {isEditButtonWithText ? t('privateBulletinStickyBar.editBulletin') : null}
              </Button>
            </Link>
          </GroupItem>
        </Group>
        <Group
          screenBreakpoint={screenBreakpoint}
          shouldGrow={isMobile}
        >
          {isManualPromoteButtonVisible && (
            <GroupItem sizeVariant={isMobile ? 'grow' : 'fixed'}>
              <ManualPromoteBulletinButton
                bulletinId={bulletin.id}
                bulletinType={bulletin.type}
                dealType={bulletin.dealType}
                viewSource={PromotePrivateBulletinViewSource.BulletinPage}
                size="extraLarge"
                mode={isUpgradePromotionPlanButtonVisible ? 'ghost' : 'primary'}
              />
            </GroupItem>
          )}
          {isUpgradePromotionPlanButtonVisible && (
            <GroupItem sizeVariant={isMobile ? 'grow' : 'fixed'}>
              <UpgradePlanButton privateUserMonetizationPopupSource={PrivateUserMonetizationSource.ListingPage} />
            </GroupItem>
          )}
          {isUnfreezeBulletinButtonVisible && (
            <GroupItem sizeVariant={isMobile ? 'grow' : 'fixed'}>
              <Button
                mode="primary"
                size="extraLarge"
                onClick={() => onUnfreezeClick(bulletin.id, bulletin.type)}
              >
                <EyeIcon width={24} height={24} />
                {t('privateBulletinStickyBar.unfreezeBulletin')}
              </Button>
            </GroupItem>
          )}
        </Group>
      </Wrapper>
    </Root>
  );
};

const mapStateToProps = (state: State) => ({
  isFreePromotionPlan: isBasicPromotionSelector(state),
  isAboutToExpire: currentPromotionPlanIsAboutToExpire(state),
});


const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => ({
  onUnfreezeClick: (bulletinId: string, bulletinType: 'bulletin' | 'commercialBulletin') => (
    dispatch(editListingStatusAction({
      id: bulletinId,
      type: bulletinType,
      status: BulletinStatusType.Displayed,
      source: UpdateListingStatusModalSource.ListingPage,
    }))
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(PrivateBulletinBar);
